import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pmsKeys } from '../queryKeys';
import { categoriesApi } from './categories.api';
import type { CreateCategoryPayload, UpdateCategoryPayload } from '../types';

export function usePmsCategories() {
  return useQuery({
    queryKey: pmsKeys.categories(),
    queryFn: () => categoriesApi.list(),
    staleTime: 5 * 60 * 1000, // 5 min — categories change rarely
    select: (d) =>
      (Array.isArray(d)
        ? d
        : ((d as { data?: unknown })?.data ?? [])) as import('../types').CompetencyCategory[],
  });
}

export function useCreatePmsCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCategoryPayload) => categoriesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.categories() }),
  });
}

export function useUpdatePmsCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, data }: { categoryId: string; data: UpdateCategoryPayload }) =>
      categoriesApi.update(categoryId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: pmsKeys.categories() }),
  });
}

export function useDeletePmsCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) => categoriesApi.delete(categoryId),
    onSuccess: () => {
      // Deleting a category can affect competencies referencing it.
      qc.invalidateQueries({ queryKey: pmsKeys.categories() });
      qc.invalidateQueries({ queryKey: pmsKeys.competenciesAll() });
    },
  });
}
